import { createActionGroup, createReducer, createSelector, emptyProps, on, props } from '@ngrx/store';
import { IAppState } from '../index.state';
import { EAuthRole, IAuthResp } from './auth.model';
import { environment } from '../../../environments/environment';
import {
  ECandidateDocument,
  ICandidate,
} from '../../../../../admin/src/app/dashboard/intermediary-desk/cifm/candidates/candidate.model';
import { EUserRole, EUserType, IUser, EUserGroup, ELicensesCat, UserGroupMapping } from '../user/user.model';
import { ICompany } from '../company/company.model';
import { EVFunctions } from 'ets-fe-ng-sdk';
import { ITiedAgent } from '../../../../../admin/src/app/dashboard/intermediary-desk/extras/agent.model';
import { IUserGroup } from '../user-group/user-group.model';
import { IDocumentMetadata } from '../../models/document.model';
import { IWorkflowApplicant } from '../../../../../crm/src/app/application-desk/application.model';

const UserTypeHeirarchy: Record<EUserType, number> = {
  [EUserType.superAdmin]: 1,
  [EUserType.admin]: 2,
  [EUserType.ciin]: 3,
  [EUserType.bpp]: 3,
  [EUserType.lossAdjuster]: 3,
  [EUserType.broker]: 3,
  [EUserType.cifm]: 3,
  [EUserType.nia]: 3,
  [EUserType.company]: 3,
  [EUserType.arian]: 3,
  [EUserType.candidate]: 4,
};

export function getUserType(
  user?: Partial<IUser>,
  hasSuperAdminAuthority = false,
): EUserType | null | undefined {
  if (hasSuperAdminAuthority || user?.userRoleType == EUserRole.superAdmin) return EUserType.superAdmin;
  if (!user) return null;
  if (user.restrictedAccess === false) return EUserType.admin;

  const licenseCat = user.licenseCat;
  if (licenseCat == ELicensesCat.company || (user.company && !licenseCat)) return EUserType.company;
  if (licenseCat == ELicensesCat.lossAdjuster) return EUserType.lossAdjuster;
  if (licenseCat == ELicensesCat.broker) return EUserType.broker;

  const userGroup = user.userGroup?.toUpperCase();
  // debugger;
  return UserGroupMapping[userGroup as EUserGroup]?.userType;
}

export function getUserOwner(
  user?: Partial<IUser>,
  hasSuperAdminAuthority = false,
): string | null | undefined {
  return getUserType(user, hasSuperAdminAuthority);
}

export namespace AuthStore {
  //#region STORE
  type ICandidateRegistrationDocuments = {
    code: ECandidateDocument;
    mandatory: boolean;
    document?: IDocumentMetadata;
  }[];
  export interface IState {
    authObj?: IAuthResp;
    candidate?: ICandidate;
    publicPortalIdentityResolved: boolean;
    publicPortalIdentityError?: string;
    candidateRegistrationDocuments: ICandidateRegistrationDocuments;
    /**Candidate's Agent Info */
    candidateAgent?: ITiedAgent;
    applicant?: IWorkflowApplicant;
    user?: IUser;
    userGroup?: IUserGroup;
    userCompany?: ICompany;
    approvedForPlatform: boolean;
  }

  export const initialState: IState = {
    publicPortalIdentityResolved: false,
    candidateRegistrationDocuments: [],
    approvedForPlatform: false,
  };
  //#endregion

  //#region ACTIONS
  export const actions = createActionGroup({
    source: 'Auth API',
    events: {
      getFromLocalAuthObj: emptyProps(),
      saveToLocalAuthObj: props<{ response: IAuthResp }>(),
      saveToLocalAuthObjSuccess: emptyProps(),
      setAuthObj: props<{ response: IAuthResp }>(),

      getFromLocalCandidate: emptyProps(),
      getFromOnlineCandidate: props<{ email?: string }>(),
      // getFromOnlineCandidateSuccess:emptyProps(),
      setCandidate: props<{ response?: ICandidate }>(),

      getFromLocalApplicant: emptyProps(),
      getFromOnlineApplicant: props<{ email?: string }>(),
      setApplicant: props<{ response?: IWorkflowApplicant }>(),
      getFromOnlinePublicPortalIdentities: props<{ email?: string }>(),
      setPublicPortalIdentities: props<{
        candidate?: ICandidate;
        applicant?: IWorkflowApplicant;
      }>(),
      setPublicPortalIdentityError: props<{ message: string }>(),

      getFromLocalCandidateAgent: emptyProps(),
      getFromOnlineCandidateAgent: emptyProps(),
      savedCandidateAgent: emptyProps(),
      getFromOnlineCandidateAgentSuccess: props<{ response?: ITiedAgent }>(),
      setCandidateAgent: props<{ response?: ITiedAgent }>(),

      getFromLocalCandidateRegDocuments: emptyProps(),
      getFromOnlineCandidateRegDocuments: emptyProps(),
      savedCandidateRegDocuments: emptyProps(),
      getFromOnlineCandidateRegDocumentsSuccess: props<{
        response?: ICandidateRegistrationDocuments;
      }>(),
      setCandidateRegDocuments: props<{
        response?: ICandidateRegistrationDocuments;
      }>(),

      getFromLocalUserGroup: emptyProps(),
      getFromOnlineUserGroup: props<{ userGroup?: string }>(),
      savedUserGroup: emptyProps(),
      setUserGroup: props<{ response?: IUserGroup }>(),
      getFromOnlineUserGroupSuccess: props<{ response?: IUserGroup }>(),

      getFromLocalUser: emptyProps(),
      getFromOnlineUser: emptyProps(),
      setUser: props<{ response?: IUser }>(),
      setUserSuccess: emptyProps(),

      setApprovedForPlatform: props<{ approvedForPlatform: boolean }>(),

      getUserCompany: emptyProps(),
      setUserCompany: props<{ response?: ICompany }>(),

      clearSession: emptyProps(),
      logout: props<{ redirectRoute?: string }>(),
      logoutSuccess: emptyProps(),
    },
  });
  //#endregion

  //#region SELECTORE
  export namespace Selectors {
    const select = (state: IAppState) => state.auth;
    export const isLoggedIn = createSelector(select, (state) => !!state.authObj?.token);
    export const token = createSelector(select, (state) => state.authObj?.token);
    export const authObj = createSelector(select, (state) => state.authObj);

    export const candidate = createSelector(
      select,
      environment.production
        ? (state) => state.candidate
        : // (state) => <IUser>{ ...state.user, userRoleType: EUserRole.superAdmin },
        // (state) => <ICandidate>{ ...state.candidate, ciinRedg: true },
        (state) => state.candidate,
    );

    export const candidateRegistrationDocuments = createSelector(
      select,
      (state) => state.candidateRegistrationDocuments,
    );
    export const candidateRegistrationDocumentsMap = createSelector(
      select,
      (state) => state.candidateRegistrationDocuments,
    );
    export const agent = createSelector(select, (state) => state.candidateAgent);
    export const applicant = createSelector(select, (state) => state.applicant);
    export const publicPortalIdentityResolved = createSelector(
      select,
      (state) => state.publicPortalIdentityResolved,
    );
    export const publicPortalIdentityError = createSelector(
      select,
      (state) => state.publicPortalIdentityError,
    );
    export const candidateNo = createSelector(candidate, (state) => state?.candidateNo);
    export const applicantNo = createSelector(applicant, (state) => state?.applicantNo);
    export const allowCandidateUpdate = createSelector(candidate, (state) => state && !state.ria);

    export const user = createSelector(
      select,
      environment.production
        ? (state) => state.user
        :
        //  (state) => <IUser>{ ...state.user, userRoleType: EUserRole.superAdmin },
        // (state) => <IUser>{ ...state.user, restrictedAccess: true, company: undefined, userGroup: 'BPP' },
        // (state) => <IUser>{ ...state.user, restrictedAccess: true, company: undefined, userGroup: 'BPP' },
        // (state) => <IUser>{ ...state.user, restrictedAccess: true, userGroup: 'CIFM', company: undefined },
        // (state) => <IUser>{ ...state.user, restrictedAccess: true, userGroup: 'CIIN' },
        // (state) => <IUser>{ ...state.user, restrictedAccess: true, userGroup: 'ARIAN' },
        // (state) => <IUser>{ ...state.user, restrictedAccess: true, company: 'C7' },
        (state) => state.user,
    );

    export const userType = createSelector(authObj, user, (authObj, user) => {
      const hasSuperAdminAuthority =
        authObj?.userDetails?.authorities?.some((x) => x.authority == EAuthRole.SUPER_ADMIN) ||
        user?.userRoleType == EUserRole.superAdmin;
      return getUserType(user, hasSuperAdminAuthority);
    });

    export const userGroup = createSelector(select, (state) => state.userGroup);

    export const approvedForPlatform = createSelector(
      select,
      userType,
      (state, userType) => state.approvedForPlatform || userType == EUserType.superAdmin,
    );
    export const userName = createSelector(user, (state) => state?.userName);

    export const userCompany = createSelector(select, (state) => state.userCompany);
    export const userCompanyCodeDescription = createSelector(user, userCompany, (user, company) =>
      EVFunctions.strConcatenator2(user?.company, company?.description),
    );

    export const checkableUserTypes = createSelector(userType, (userType): EUserType[] => {
      if (!userType) return [];
      const res: EUserType[] = [],
        myOrder = UserTypeHeirarchy[userType];
      // console.log('userType',userType)
      for (const key in UserTypeHeirarchy)
        if (
          Object.prototype.hasOwnProperty.call(UserTypeHeirarchy, key) &&
          (UserTypeHeirarchy[key as EUserType] > myOrder || userType == key)
        )
          res.push(key as EUserType);
      return res;
    });
  }
  //#endregion

  //#region REDUCER
  export const reducer = createReducer(
    initialState,
    on(actions.setAuthObj, (state, { response: auth }) => {
      environment._token = auth?.token;
      return { ...state, authObj: auth };
    }),
    on(actions.setCandidate, (state, { response: candidate }) => ({
      ...state,
      candidate,
    })),
    on(actions.setApplicant, (state, { response: applicant }) => ({
      ...state,
      applicant,
    })),
    on(actions.getFromOnlinePublicPortalIdentities, (state) => ({
      ...state,
      publicPortalIdentityResolved: false,
      publicPortalIdentityError: undefined,
    })),
    on(actions.setPublicPortalIdentities, (state, { candidate, applicant }) => ({
      ...state,
      candidate,
      applicant,
      publicPortalIdentityResolved: true,
      publicPortalIdentityError: undefined,
    })),
    on(actions.setPublicPortalIdentityError, (state, { message }) => ({
      ...state,
      publicPortalIdentityResolved: false,
      publicPortalIdentityError: message,
    })),
    on(actions.setUserCompany, (state, { response: userCompany }) => ({
      ...state,
      userCompany,
    })),
    on(actions.setUser, (state, { response: user }) => ({
      ...state,
      user: user,
      approvedForPlatform: !!user,
    })),
    on(actions.setCandidateAgent, (state, { response }) => ({
      ...state,
      candidateAgent: response,
    })),
    on(actions.setApprovedForPlatform, (state, { approvedForPlatform }) => ({
      ...state,
      approvedForPlatform,
    })),
    on(actions.setCandidateRegDocuments, (state, { response }) => ({
      ...state,
      candidateRegistrationDocuments: response || [],
    })),
    on(actions.setUserGroup, (state, { response }) => ({
      ...state,
      userGroup: response,
    })),
    on(actions.clearSession, () => ({
      ...initialState,
    })),
    // on(actions.logout, (state) => ({
    //   ...state,
    //   authObj: undefined,
    //   candidate: undefined,
    //   user: undefined,
    //   userGroup: undefined,
    //   candidateAgent: undefined,
    // })),
  );
  //#endregion
}
